// src/components/AiChatBot.tsx

import { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, Paper, Stack, TextField, IconButton, Avatar,
    useTheme, keyframes, Drawer, Fab, Badge, CircularProgress, Chip,
} from '@mui/material';

import SendIcon from '@mui/icons-material/Send';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { encodeId } from '../utils/idEncoder';

const API_BASE = 'https://apistaging.workhorse.com.sg/ai';

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

interface VehicleUI {
    match_score: number | null;
    price_rating: string | null;
    is_best_match: boolean;
    pills: string[];
}

interface Vehicle {
    system_id: string;
    vehicle_id: number;
    condition: string;
    brand_model: string;
    body_type: string;
    transmission: string;
    fuel: string;
    price: number;
    image_url: string;
    description: string;
    ui: VehicleUI;
}

interface Requirement {
    icon_type: 'type' | 'budget' | 'payload' | 'fuel' | 'usage' | 'must_have';
    label: string;
    value: string;
}

interface Message {
    role: 'system' | 'ai' | 'user';
    content?: string;
    vehicles?: Vehicle[];
    requirements?: Requirement[];
}

const AiChatBot = () => {
    const theme = useTheme();
    const chatEndRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);

    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'ai',
            content: 'Hello! I am your WorkHorse AI Assistant. Tell me your budget, preferred body type, or what you will be using the vehicle for!'
        }
    ]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
    }, [messages, loading, isOpen]);

    const formatPrice = (price: number) => price > 0 ? `$${price.toLocaleString()}` : 'POA';
    const handleToggle = () => setIsOpen(!isOpen);

    const getRequirementIcon = (type: string) => {
        const props = { fontSize: "small" as const, sx: { color: '#FFC107' } };
        switch (type) {
            case 'type': return <DirectionsCarIcon {...props} />;
            case 'budget': return <AttachMoneyIcon {...props} />;
            case 'payload': return <LocalShippingIcon {...props} />;
            case 'fuel': return <LocalGasStationIcon {...props} />;
            case 'usage': return <ShowChartIcon {...props} />;
            default: return <CheckCircleOutlineIcon {...props} />;
        }
    };

    const handleSend = async () => {
        if (!input.trim()) return;
        const userMsg = input.trim();
        
        // Add the user's message to the UI instantly
        const updatedMessages = [...messages, { role: 'user' as const, content: userMsg }];
        setMessages(updatedMessages);
        setInput('');
        setLoading(true);

        // Build the history payload for the backend
        const historyPayload = updatedMessages.map(m => ({
            role: m.role === 'ai' ? 'assistant' : m.role,
            content: m.content || ''
        }));

        try {
            const response = await fetch(`${API_BASE}/ai-search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: historyPayload })
            });

            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();

            // Handle pure conversation vs search results
            if (data.is_conversational) {
                setMessages(prev => [...prev, {
                    role: 'ai',
                    content: data.bot_reply
                }]);
            } else {
                setMessages(prev => [...prev, {
                    role: 'ai',
                    content: data.bot_reply,
                    vehicles: data.matched_vehicles || [],
                    requirements: data.requirements || undefined
                }]);
            }

        } catch (error) {
            setMessages(prev => [...prev, { role: 'ai', content: '⚠️ Sorry, I am having trouble connecting to my servers right now.' }]);
        } finally {
            setLoading(false);
        }
    };

    const formatMessage = (text: string) => text ? text.replace(/\n/g, '<br/>').replace(/\*\*(.*?)\*\*/g, '<b>$1</b>') : "";

    return (
        <>
            {!isOpen && (
                <Box sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
                    <Badge color="error" variant="dot" sx={{ '& .MuiBadge-badge': { width: 12, height: 12, borderRadius: '50%', border: '2px solid white' } }}>
                        <Fab onClick={handleToggle} sx={{ bgcolor: '#FFC107', color: '#000', '&:hover': { bgcolor: '#FFB300' }, width: 65, height: 65 }}>
                            <ChatIcon />
                        </Fab>
                    </Badge>
                </Box>
            )}

            <Drawer
                anchor="right"
                open={isOpen}
                onClose={handleToggle}
                sx={{ '& .MuiDrawer-paper': { width: { xs: '100vw', sm: 500 }, boxSizing: 'border-box', bgcolor: '#fff' } }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee' }}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <AutoAwesomeIcon sx={{ color: '#FF8E00' }} />
                            <Typography fontWeight="bold">WorkHorse AI</Typography>
                            <Chip label="Pro" size="small" sx={{ bgcolor: '#FFC107', color: '#000', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                        </Box>
                    </Box>
                    <Box>
                        <IconButton size="small" onClick={() => setMessages([{ role: 'ai', content: 'Hello! I am your WorkHorse AI Assistant. Tell me your budget, preferred body type, and what you will be using the vehicle for!' }])}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={handleToggle}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>

                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, md: 3 }, bgcolor: '#fff' }}>
                    <Stack spacing={3}>
                        {messages.map((msg, index) => (
                            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>

                                {msg.role === 'ai' && (msg.content || msg.requirements) && (
                                    <AutoAwesomeIcon sx={{ color: '#FF8E00' }} />
                                )}

                                {msg.requirements && msg.requirements.length > 0 && (
                                    <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #FFEDD5', bgcolor: '#FFFAED', width: '100%', mb: 1 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#D97706' }}>Your Requirements</Typography>
                                            <Typography variant="caption" fontWeight="bold" sx={{ color: '#D97706', cursor: 'pointer' }}>Edit</Typography>
                                        </Box>
                                        <Stack spacing={1.5}>
                                            {msg.requirements.map((req, i) => (
                                                <Box key={i} display="flex" gap={1.5} alignItems="flex-start">
                                                    <Box sx={{ mt: 0.3 }}>{getRequirementIcon(req.icon_type)}</Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">{req.label}</Typography>
                                                        <Typography variant="body2" fontWeight="500">{req.value}</Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}

                                {msg.content && (
                                    <Paper elevation={0} sx={{
                                        p: 2, borderRadius: 3, maxWidth: '85%',
                                        borderTopLeftRadius: msg.role === 'ai' ? 4 : 24,
                                        borderTopRightRadius: msg.role === 'user' ? 4 : 24,
                                        bgcolor: msg.role === 'user' ? '#FFC107' : '#F3F4F6',
                                        color: msg.role === 'user' ? '#000' : '#111827',
                                    }}>
                                        <Typography variant="body2" sx={{ lineHeight: 1.5, fontWeight: msg.role === 'user' ? 500 : 400 }} dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }} />
                                    </Paper>
                                )}

                                {msg.vehicles && msg.vehicles.length > 0 && (
                                    <Stack spacing={2} sx={{ width: '100%', mt: 1 }}>
                                        {msg.vehicles.map((v, vIdx) => (
                                            <Paper
                                                key={vIdx}
                                                elevation={0}
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 3,
                                                    border: '1px solid #E5E7EB',
                                                    display: 'flex',
                                                    gap: 2,
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    '&:hover': { borderColor: '#FFC107', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' },
                                                    transition: 'all 0.2s'
                                                }}
                                                onClick={() => window.open(`/used-vehicle-details/${encodeId(v.vehicle_id)}`, '_blank')}
                                            >
                                                <Box sx={{ width: 120, height: 90, borderRadius: 2, overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
                                                    {v.ui?.is_best_match && (
                                                        <Box sx={{ position: 'absolute', top: 0, left: 0, bgcolor: '#FFC107', color: '#000', fontSize: '0.6rem', fontWeight: 'bold', px: 1, py: 0.3, borderBottomRightRadius: 6, zIndex: 1 }}>
                                                            BEST MATCH
                                                        </Box>
                                                    )}
                                                    <img src={v.image_url || 'https://via.placeholder.com/300x200?text=No+Image'} alt={v.brand_model} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                </Box>

                                                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{v.brand_model}</Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                            {v.condition} • {v.fuel} • {v.transmission}
                                                        </Typography>
                                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                                            {v.ui?.pills?.map((pill, pIdx) => (
                                                                <Chip key={pIdx} label={pill} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#F3F4F6', color: '#4B5563' }} />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                    <Stack direction="row" alignItems="baseline" spacing={1}>
                                                        <Typography variant="body1" fontWeight="bold">{formatPrice(v.price)}</Typography>
                                                        {v.ui?.price_rating && v.ui.price_rating !== "None" && (
                                                            <Typography variant="caption" sx={{ color: v.ui.price_rating.includes('Great') ? '#10B981' : '#F59E0B', fontWeight: 'bold' }}>
                                                                {v.ui.price_rating}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Box>

                                                {v.ui?.match_score && (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 60, flexShrink: 0 }}>
                                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                            <CircularProgress variant="determinate" value={100} size={48} thickness={4} sx={{ color: '#F3F4F6' }} />
                                                            <CircularProgress variant="determinate" value={v.ui.match_score} size={48} thickness={4} sx={{ color: '#10B981', position: 'absolute', left: 0 }} />
                                                            <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <Typography variant="caption" fontWeight="bold" component="div" color="text.primary">{`${v.ui.match_score}%`}</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.5 }}>Match Score</Typography>
                                                    </Box>
                                                )}
                                            </Paper>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        ))}

                        {loading && (
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                <AutoAwesomeIcon sx={{ color: '#FF8E00' }} />
                                <Paper elevation={0} sx={{ px: 2, py: 2.5, borderRadius: 3, borderTopLeftRadius: 4, bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#9CA3AF', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.32s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#9CA3AF', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.16s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#9CA3AF', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both` }} />
                                </Paper>
                            </Box>
                        )}
                        <div ref={chatEndRef} />
                    </Stack>
                </Box>

                <Box sx={{ p: 2, borderTop: '1px solid #eee', bgcolor: '#fff' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            fullWidth placeholder="Type your requirements..." variant="outlined" size="small"
                            value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '50px', bgcolor: '#F9FAFB' } }}
                        />
                        <IconButton
                            onClick={handleSend} disabled={loading || !input.trim()}
                            sx={{ bgcolor: '#FFC107', color: '#000', width: 40, height: 40, '&:hover': { bgcolor: '#FFB300' }, '&.Mui-disabled': { bgcolor: '#E5E7EB', color: '#9CA3AF' } }}
                        >
                            <SendIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                </Box>
            </Drawer>
        </>
    );
};

export default AiChatBot;